// Sprint F9 — Fluxo de Caixa
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine, Cell,
} from 'recharts';
import {
  Card, Segmented, Select, Button, Modal, Tag, Typography, Space, Alert,
  Row, Col, Statistic, Empty, Form, Input, InputNumber, DatePicker,
  App as AntApp, Flex, Table, Tooltip as AntTooltip,
} from 'antd';
import {
  SettingOutlined, RightOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import { API_URL } from '../config';
import { fmtDataBr } from '../utils/dataFmt';
import { PROPS_DINHEIRO_BR } from '../utils/dinheiroInput';

const { Title, Text } = Typography;

async function getToken(): Promise<string> {
  const s = await fetchAuthSession();
  return s.tokens?.idToken?.toString() ?? '';
}

interface Ponto {
  bucket: string;
  entradas: number;
  saidas: number;
  liquido: number;
  saldo_acumulado: number | null;
  entradas_prev?: number;
  saidas_prev?: number;
  saldo_projetado?: number | null;
  // Transferências entre contas (2026-07-16): informativas, líquido zero —
  // aparecem só na tabela, não no gráfico nem nos totais.
  transf?: number;
  transf_n?: number;
  // FZ 5.3 — visão por conta: transferências viram movimento real da conta
  // (entrada quando destino, saída quando origem) e entram no líquido/saldo.
  transf_in?: number;
  transf_out?: number;
}
interface Totais {
  entradas: number;
  saidas: number;
  liquido: number;
  saldo_inicial: number;
  saldo_final: number;
  entradas_previstas?: number;
  saidas_previstas?: number;
  saldo_projetado_final?: number;
}
interface SaldoOrigem {
  modo: 'historico' | 'roll_back' | 'corte_exato' | 'forward_corte' | 'reverso_corte'
    | 'conta_ancora_extrato' | 'conta_sem_ancora';
  data_ref?: string;
  data_corte?: string;
  saldo_corte?: number;
  ajuste_aplicado?: number;
  saldo_em_data_ref?: number;
  entradas_no_intervalo?: number;
  saidas_no_intervalo?: number;
  entradas_acumuladas?: number;
  saidas_acumuladas?: number;
}
interface FluxoResp {
  modo: 'diario' | 'mensal';
  periodo: { data_ini: string; data_fim: string };
  pontos: Ponto[];
  totais: Totais;
  saldo_origem?: SaldoOrigem | null;
  // FZ 5.3/5.4 — presente só na visão por conta
  conta?: {
    id: string; apelido: string; tipo: string;
    extrato: { saldo: number; ate: string } | null;
    sem_conta: {
      realizado_cr: number; realizado_cp: number;
      previsto_cr: number; previsto_cp: number;
      emprestimos_previstos: number;
    };
  };
}
interface ContaFin {
  id: string; apelido: string | null; nome: string; tipo: string; ordem: number;
}
interface Lancamento {
  id: string;
  tipo: 'entrada' | 'saida';
  origem: string;
  // Previsto = ainda não passou pelo banco. Vem com a MESMA data de caixa que
  // o gráfico usa na projeção; data_base é o vencimento/emissão de origem.
  previsto?: boolean;
  data: string;
  data_base?: string | null;
  parcela_titulo: string | null;
  descricao: string | null;
  contraparte: string | null;
  contrato_numero: string | null;
  valor: number;
  status: string;
  nota_fiscal_numero: string | null;
}

const fmt = (v: any) => {
  const n = typeof v === 'number' ? v : parseFloat(v);
  if (isNaN(n)) return 'R$ 0';
  return n.toLocaleString('pt-BR', {
    style: 'currency', currency: 'BRL', maximumFractionDigits: 0,
  });
};
const fmtCheio = (v: any) => {
  const n = typeof v === 'number' ? v : parseFloat(v);
  if (isNaN(n)) return 'R$ 0,00';
  return n.toLocaleString('pt-BR', {
    style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
};
const _hojeMes = () => new Date().toISOString().slice(0, 7);
const _hojeAno = () => new Date().getFullYear();

function _labelBucket(bucket: string, modo: 'diario' | 'mensal'): string {
  if (modo === 'diario') {
    const d = bucket.slice(8, 10); return d;
  }
  const m = parseInt(bucket.slice(5, 7));
  const ano2 = bucket.slice(2, 4);
  const meses = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
  return `${meses[m - 1]}/${ano2}`;
}

function FinanceiroFluxoCaixaInner() {
  const { message } = AntApp.useApp();
  const [modo, setModo] = useState<'diario' | 'mensal'>('diario');
  const [mes, setMes] = useState<string>(_hojeMes());
  const [ano, setAno] = useState<number>(_hojeAno());
  const [resp, setResp] = useState<FluxoResp | null>(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const [drillData, setDrillData] = useState<string | null>(null);
  const [drillLancs, setDrillLancs] = useState<Lancamento[]>([]);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillTotais, setDrillTotais] = useState<{
    realizado_entradas: number; realizado_saidas: number;
    previsto_entradas: number; previsto_saidas: number;
  } | null>(null);

  const [saldoVigente, setSaldoVigente] = useState<{ valor: number; data_ref: string } | null>(null);
  const [editandoSaldo, setEditandoSaldo] = useState(false);
  const [saldoForm, setSaldoForm] = useState({ valor: '', data_ref: '', observacao: '' });

  // Data de corte (condição de contorno do fluxo)
  const [corteCfg, setCorteCfg] = useState<{ data_corte: string; saldo_corte: { valor: string; data_ref: string } | null } | null>(null);
  const [editandoCorte, setEditandoCorte] = useState(false);
  const [corteForm, setCorteForm] = useState({ data_corte: '', valor: '', observacao: '' });

  // FZ 5.3 — visão por conta: null = consolidado (todas as contas)
  const [contas, setContas] = useState<ContaFin[]>([]);
  const [contaSel, setContaSel] = useState<string | null>(null);

  async function carregar() {
    setLoading(true); setErro(null);
    try {
      const token = await getToken();
      const params = (modo === 'diario'
        ? `?modo=diario&mes=${mes}`
        : `?modo=mensal&ano=${ano}`)
        + (contaSel ? `&conta_id=${contaSel}` : '');
      const r = await axios.get(`${API_URL}/fluxo-caixa${params}`,
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setResp(d);
    } catch (e: any) {
      setErro(e.response?.data?.error || e.message);
    } finally { setLoading(false); }
  }

  async function carregarContas() {
    try {
      const token = await getToken();
      const r = await axios.get(`${API_URL}/financeiro/contas`,
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setContas(Array.isArray(d) ? d : []);
    } catch { /* seletor fica só com "Todas" */ }
  }

  async function carregarSaldo() {
    try {
      const token = await getToken();
      const r = await axios.get(`${API_URL}/saldo-inicial`,
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      if (d?.vigente) {
        setSaldoVigente({
          valor: parseFloat(d.vigente.valor) || 0,
          data_ref: String(d.vigente.data_ref).slice(0, 10),
        });
      } else {
        setSaldoVigente({ valor: 0, data_ref: '' });
      }
    } catch { /* ignore */ }
  }

  async function carregarCorte() {
    try {
      const token = await getToken();
      const r = await axios.get(`${API_URL}/saldo-corte`,
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setCorteCfg(d);
      setCorteForm({
        data_corte: d?.data_corte || '2026-01-01',
        valor: d?.saldo_corte ? String(d.saldo_corte.valor) : '',
        observacao: '',
      });
    } catch { /* ignore */ }
  }

  async function salvarCorte() {
    if (!corteForm.data_corte) {
      message.warning('Data de corte é obrigatória.');
      return;
    }
    try {
      const token = await getToken();
      const payload: any = { data_corte: corteForm.data_corte };
      if (corteForm.valor !== '') payload.valor = parseFloat(corteForm.valor);
      if (corteForm.observacao) payload.observacao = corteForm.observacao;
      await axios.put(`${API_URL}/saldo-corte`, payload,
        { headers: { Authorization: token } });
      setEditandoCorte(false);
      message.success('Data de corte atualizada.');
      await carregarCorte();
      await carregar();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    }
  }

  useEffect(() => { carregar(); /* eslint-disable-next-line */ }, [modo, mes, ano, contaSel]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { carregarSaldo(); carregarCorte(); carregarContas(); }, []);

  const navigate = useNavigate();

  // Para onde cada linha do detalhe leva. Recebimento e previsto de CR são
  // parcelas do Contas a Receber; XLSX e previsto de CP são lançamentos do
  // Contas a Pagar. Parcela de empréstimo e lançamento manual não têm ficha
  // própria — a linha fica sem link em vez de levar a lugar nenhum.
  function destinoDoLancamento(l: Lancamento): string | null {
    if (l.origem === 'recebimento' || l.origem === 'previsto_cr') {
      return `/financeiro/contas-receber?parcela=${l.id}`;
    }
    if (l.origem === 'upload_xlsx' || l.origem === 'previsto_cp') {
      return `/financeiro/contas-pagar?conta=${l.id}`;
    }
    return null;
  }

  // Tem o que abrir no detalhe? Realizado OU previsto — o dia futuro só tem
  // previsto e continua clicável.
  const temMovimento = (p: Ponto) => (p.entradas || 0) > 0 || (p.saidas || 0) > 0
    || (p.entradas_prev || 0) > 0 || (p.saidas_prev || 0) > 0;

  async function abrirDrillDown(bucket: string) {
    setDrillData(bucket);
    setDrillLoading(true);
    setDrillLancs([]);
    setDrillTotais(null);
    try {
      const token = await getToken();
      const q = (modo === 'diario' ? `?data=${bucket}` : `?periodo=${bucket}`)
        + (contaSel ? `&conta_id=${contaSel}` : '');
      const r = await axios.get(`${API_URL}/fluxo-caixa/detalhe${q}`,
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setDrillLancs(d.lancamentos || []);
      setDrillTotais(d.totais || null);
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setDrillLoading(false); }
  }

  async function salvarSaldoInicial() {
    if (!saldoForm.data_ref || saldoForm.valor === '') {
      message.warning('Data de referência e valor são obrigatórios.');
      return;
    }
    try {
      const token = await getToken();
      await axios.put(`${API_URL}/saldo-inicial`, {
        data_ref: saldoForm.data_ref,
        valor: parseFloat(saldoForm.valor),
        observacao: saldoForm.observacao,
      }, { headers: { Authorization: token } });
      setEditandoSaldo(false);
      setSaldoForm({ valor: '', data_ref: '', observacao: '' });
      message.success('Saldo atualizado.');
      await carregarSaldo();
      await carregar();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    }
  }

  // Preenche TODOS os dias/meses do periodo (mesmo sem movimentacao) pra o grafico
  // e tabela mostrarem o intervalo completo (ex: comecar em 01/05 mesmo que a 1a
  // movimentacao tenha sido em 04/05). Saldo acumulado em dias sem movimentacao
  // herda do anterior (continua plano).
  const pontosCompletos = useMemo<Ponto[]>(() => {
    if (!resp || !resp.periodo) return [];
    const porBucket = new Map(resp.pontos.map(p => [p.bucket, p]));
    const out: Ponto[] = [];
    let saldoCorrente = resp.totais?.saldo_inicial ?? 0;
    // Projecao: em buckets >= hoje sem movimentacao a linha tracejada continua
    // plana no ultimo valor conhecido (ancora no realizado quando ainda nao ha
    // previsto). Buckets passados ficam null (sem tracejado no passado).
    let projCorrente: number | null = null;
    const hojeBucket = resp.modo === 'diario'
      ? dayjs().format('YYYY-MM-DD') : dayjs().format('YYYY-MM');
    const _push = (key: string, existing: Ponto | undefined) => {
      if (existing) {
        // saldo_acumulado vem null em bucket futuro (realizado não existe lá).
        // Guarda só o último valor real, senão o preenchimento perde a âncora.
        if (existing.saldo_acumulado != null) saldoCorrente = existing.saldo_acumulado;
        if (existing.saldo_projetado != null) projCorrente = existing.saldo_projetado;
        out.push({ ...existing, saldo_projetado: existing.saldo_projetado ?? projCorrente });
      } else {
        if (key >= hojeBucket && projCorrente === null) projCorrente = saldoCorrente;
        out.push({
          bucket: key,
          entradas: 0,
          saidas: 0,
          liquido: 0,
          // Dia futuro sem movimento: sem realizado. A linha cheia para em hoje.
          saldo_acumulado: key > hojeBucket ? null : saldoCorrente,
          entradas_prev: 0,
          saidas_prev: 0,
          transf: 0,
          transf_n: 0,
          transf_in: 0,
          transf_out: 0,
          saldo_projetado: key >= hojeBucket ? projCorrente : null,
        });
      }
    };
    if (resp.modo === 'diario') {
      let d = dayjs(resp.periodo.data_ini);
      const fim = dayjs(resp.periodo.data_fim);
      while (d.isBefore(fim) || d.isSame(fim, 'day')) {
        const key = d.format('YYYY-MM-DD');
        _push(key, porBucket.get(key));
        d = d.add(1, 'day');
      }
    } else {
      // mensal — itera meses do data_ini ate data_fim
      let d = dayjs(resp.periodo.data_ini).startOf('month');
      const fim = dayjs(resp.periodo.data_fim).startOf('month');
      while (d.isBefore(fim) || d.isSame(fim, 'month')) {
        const key = d.format('YYYY-MM');
        _push(key, porBucket.get(key));
        d = d.add(1, 'month');
      }
    }
    return out;
  }, [resp]);

  const temPrevisto = useMemo(
    () => pontosCompletos.some(p => (p.entradas_prev || 0) > 0 || (p.saidas_prev || 0) > 0),
    [pontosCompletos]
  );
  // Coluna de transferências só aparece se houve alguma no período (2026-07-16)
  const temTransf = useMemo(
    () => pontosCompletos.some(p => (p.transf || 0) > 0),
    [pontosCompletos]
  );
  // FZ 5.3 — visão por conta: transferências com lado (entram no saldo)
  const temTransfLado = useMemo(
    () => pontosCompletos.some(p => (p.transf_in || 0) > 0 || (p.transf_out || 0) > 0),
    [pontosCompletos]
  );
  // Comparador fluxo × extrato: saldo do fluxo no dia em que o extrato termina
  const comparador = useMemo(() => {
    const ex = resp?.conta?.extrato;
    if (!ex || !pontosCompletos.length) return null;
    const ateBucket = resp!.modo === 'diario' ? ex.ate : ex.ate.slice(0, 7);
    let saldoFluxo: number | null = null;
    for (const p of pontosCompletos) {
      if (p.bucket <= ateBucket && p.saldo_acumulado != null) saldoFluxo = p.saldo_acumulado;
    }
    if (saldoFluxo === null) return null;
    return { saldoExtrato: ex.saldo, ate: ex.ate, saldoFluxo, delta: +(saldoFluxo - ex.saldo).toFixed(2) };
  }, [resp, pontosCompletos]);

  const chartData = useMemo(() => {
    if (!resp) return [];
    return pontosCompletos.map(p => ({
      bucket: p.bucket,
      label: _labelBucket(p.bucket, resp.modo),
      entradas: p.entradas,
      saidas: -p.saidas,
      saldo: p.saldo_acumulado,
      entradas_prev: p.entradas_prev || 0,
      saidas_prev: -(p.saidas_prev || 0),
      saldo_proj: p.saldo_projetado ?? null,
    }));
  }, [resp, pontosCompletos]);

  const totais = resp?.totais;

  const drillCols: ColumnsType<Lancamento> = [
    {
      title: 'Tipo', dataIndex: 'tipo', key: 't', width: 130,
      render: (v, l) => (
        <div>
          {/* Previsto usa a versão vazada da mesma cor — é o eco dos tons
              claros/tracejado do gráfico: mesma natureza, ainda não aconteceu. */}
          <Tag color={v === 'entrada' ? 'success' : 'error'}
            bordered={!l.previsto}
            style={{ margin: 0, opacity: l.previsto ? 0.55 : 1,
              borderStyle: l.previsto ? 'dashed' : 'solid',
              borderWidth: 1, borderColor: v === 'entrada' ? '#0a7f4a' : '#B91626' }}>
            {v === 'entrada' ? '↓ entrada' : '↑ saída'}
          </Tag>
          <div style={{ fontSize: 9, color: '#888', marginTop: 2 }}>
            {l.origem === 'upload_xlsx' ? '📊 XLSX'
             : l.origem === 'manual' ? '✍ Manual'
             : l.origem === 'recebimento' ? '💰 Recebimento'
             : l.origem === 'previsto_cr' ? '🕒 A receber'
             : l.origem === 'previsto_cp' ? '🕒 A pagar'
             : l.origem === 'previsto_emprestimo' ? '🕒 Empréstimo' : l.origem}
          </div>
        </div>
      ),
    },
    {
      title: 'Data', dataIndex: 'data', key: 'd', width: 110,
      render: (v, l) => (
        <div>
          <span style={{ opacity: l.previsto ? 0.7 : 1 }}>{fmtDataBr(String(v).slice(0, 10))}</span>
          {l.previsto && (
            <div style={{ fontSize: 9, color: '#888', marginTop: 2 }}>
              previsto
              {l.data_base && String(l.data_base).slice(0, 10) !== String(v).slice(0, 10)
                && ` · vence ${fmtDataBr(String(l.data_base).slice(0, 10))}`}
            </div>
          )}
        </div>
      ),
    },
    { title: 'Contraparte', dataIndex: 'contraparte', key: 'c', render: (v) => v || '—' },
    {
      title: 'Descrição', key: 'desc',
      render: (_, l) => {
        const destino = destinoDoLancamento(l);
        const corpo = (
          <>
            {l.parcela_titulo && <Text strong>{l.parcela_titulo}</Text>}
            {l.parcela_titulo && l.descricao && <br />}
            {l.descricao}
          </>
        );
        return (
          <div>
            {destino
              ? <Button type="link" style={{ padding: 0, height: 'auto', textAlign: 'left',
                  whiteSpace: 'normal' }}
                  title="Abrir a ficha deste lançamento"
                  onClick={() => { setDrillData(null); navigate(destino); }}>
                  {corpo}
                </Button>
              : corpo}
            {l.contrato_numero && (
              <div style={{ fontSize: 9, color: '#888', marginTop: 2 }}>
                Contrato {l.contrato_numero}
                {l.nota_fiscal_numero && ` · NF ${l.nota_fiscal_numero}`}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Valor', dataIndex: 'valor', key: 'v', align: 'right', width: 120,
      render: (v, l) => (
        <Text strong={!l.previsto}
          style={{ color: l.tipo === 'entrada' ? '#0a7f4a' : '#B91626',
            opacity: l.previsto ? 0.7 : 1 }}>
          {fmtCheio(Math.abs(v))}
        </Text>
      ),
    },
    { title: 'Status', dataIndex: 'status', key: 's', width: 110 },
  ];

  const pontosCols: ColumnsType<Ponto> = [
    {
      title: modo === 'diario' ? 'Data' : 'Mês', dataIndex: 'bucket', key: 'b',
      render: (v) => modo === 'diario' ? fmtDataBr(v) : _labelBucket(v, 'mensal'),
    },
    {
      title: 'Entradas', dataIndex: 'entradas', key: 'e', align: 'right',
      render: (v, p) => (
        <div>
          {v > 0 ? <Text style={{ color: '#0a7f4a' }}>{fmtCheio(v)}</Text> : '—'}
          {(p.entradas_prev || 0) > 0 && (
            <div style={{ fontSize: 10, color: '#8fd4b0' }}>prev. {fmtCheio(p.entradas_prev || 0)}</div>
          )}
        </div>
      ),
    },
    {
      title: 'Saídas', dataIndex: 'saidas', key: 's', align: 'right',
      render: (v, p) => (
        <div>
          {v > 0 ? <Text style={{ color: '#B91626' }}>{fmtCheio(v)}</Text> : '—'}
          {(p.saidas_prev || 0) > 0 && (
            <div style={{ fontSize: 10, color: '#eba3ab' }}>prev. {fmtCheio(p.saidas_prev || 0)}</div>
          )}
        </div>
      ),
    },
    // Transferências entre contas: informativas (líquido zero) — só na
    // tabela, por pedido do Lucas (2026-07-16); o gráfico não muda.
    ...(temTransf ? [{
      title: '🔁 Transferências', dataIndex: 'transf', key: 'tr', align: 'right' as const,
      render: (v: number, p: Ponto) => (v || 0) > 0 ? (
        <AntTooltip title={`${p.transf_n} transferência${(p.transf_n || 0) > 1 ? 's' : ''} entre contas — não afeta o resultado`}>
          <Text type="secondary">{fmtCheio(v)}</Text>
        </AntTooltip>
      ) : <Text type="secondary">—</Text>,
    }] : []),
    // FZ 5.3 — visão por conta: transferências com LADO, entram no líquido
    // e no saldo da conta (+recebida de outra conta / −enviada pra outra).
    ...(temTransfLado ? [{
      title: '🔁 Transf. da conta', key: 'trl', align: 'right' as const,
      render: (_: any, p: Ponto) => {
        const tin = p.transf_in || 0; const tout = p.transf_out || 0;
        if (tin === 0 && tout === 0) return <Text type="secondary">—</Text>;
        return (
          <div>
            {tin > 0 && <div><Text style={{ color: '#0a7f4a' }}>+{fmtCheio(tin)}</Text></div>}
            {tout > 0 && <div><Text style={{ color: '#B91626' }}>−{fmtCheio(tout)}</Text></div>}
          </div>
        );
      },
    }] : []),
    {
      title: 'Líquido', dataIndex: 'liquido', key: 'l', align: 'right',
      render: (v) => <Text strong style={{ color: v >= 0 ? '#0a7f4a' : '#B91626' }}>{fmtCheio(v)}</Text>,
    },
    {
      title: 'Saldo acumulado', dataIndex: 'saldo_acumulado', key: 'sa', align: 'right',
      render: (v) => <Text strong style={{ color: v >= 0 ? '#0930D0' : '#B91626' }}>{fmtCheio(v)}</Text>,
    },
    ...(temPrevisto ? [{
      title: 'Saldo projetado', dataIndex: 'saldo_projetado', key: 'sp', align: 'right' as const,
      render: (v: number | null) => v == null ? <Text type="secondary">—</Text>
        : <Text style={{ color: v >= 0 ? '#0930D0' : '#B91626', fontStyle: 'italic' }}>{fmtCheio(v)}</Text>,
    }] : []),
    {
      title: '', key: 'v', width: 50, align: 'center',
      render: () => <RightOutlined style={{ color: '#0930D0' }} />,
    },
  ];

  return (
    <div style={{ padding: '16px 24px', maxWidth: 1400, margin: '0 auto' }}>
      <Flex justify="space-between" align="flex-start" wrap="wrap" gap={16} style={{ marginBottom: 16 }}>
        <div>
          <Title level={4} style={{ margin: 0, color: '#020D7F' }}>💰 Fluxo de Caixa</Title>
          <Text type="secondary" style={{ fontSize: 11 }}>
            Entradas (recebimentos) − Saídas (pagamentos) por {modo === 'diario' ? 'dia' : 'mês'}.
            Saldo acumulado parte de R$ {(saldoVigente?.valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            {saldoVigente?.data_ref && ` (vigente desde ${fmtDataBr(saldoVigente.data_ref)})`}.
            {temPrevisto && ' Tons claros e linha tracejada: previsto na data estimada de caixa (NF a emitir: emissão prevista + prazo; boleto na rua: vencimento; vencido: hoje).'}
          </Text>
        </div>
        <Space wrap>
          <Select
            value={contaSel ?? '__todas__'}
            onChange={(v) => setContaSel(v === '__todas__' ? null : v)}
            size="small"
            style={{ minWidth: 180 }}
            options={[
              { value: '__todas__', label: '🏦 Todas as contas' },
              ...contas.map(c => ({
                value: c.id,
                label: `${c.tipo === 'cartao' ? '💳' : c.tipo === 'investimento' ? '📈' : c.tipo === 'caixa' ? '💵' : '🏦'} ${c.apelido || c.nome}`,
              })),
            ]}
          />
          <Segmented
            value={modo}
            onChange={(v) => setModo(v as 'diario' | 'mensal')}
            options={[{ label: 'Diário', value: 'diario' }, { label: 'Mensal', value: 'mensal' }]}
            size="small"
          />
          {modo === 'diario' ? (
            <DatePicker.MonthPicker
              value={mes ? dayjs(mes + '-01') : null}
              onChange={(d: Dayjs | null) => setMes(d ? d.format('YYYY-MM') : '')}
              size="small"
              format="MM/YYYY"
              allowClear={false}
            />
          ) : (
            <Select
              value={ano}
              onChange={setAno}
              options={[2024, 2025, 2026, 2027].map(y => ({ value: y, label: String(y) }))}
              size="small"
              style={{ width: 90 }}
            />
          )}
          <Button icon={<SettingOutlined />} onClick={() => setEditandoCorte(true)} size="small">
            Data de corte
          </Button>
          <Button icon={<SettingOutlined />} onClick={() => setEditandoSaldo(true)} size="small">
            Saldo inicial
          </Button>
        </Space>
      </Flex>

      {erro && (
        <Alert type="error" message={`Erro: ${erro}`} closable onClose={() => setErro(null)} style={{ marginBottom: 12 }} />
      )}

      {/* FZ 5.3/5.4 — visão por conta: comparador fluxo × extrato + lacunas */}
      {resp?.conta && (
        <Alert
          type={comparador && Math.abs(comparador.delta) > 1 ? 'warning' : 'info'}
          showIcon
          style={{ marginBottom: 12 }}
          message={
            <Text style={{ fontSize: 12 }}>
              <strong>Conta: {resp.conta.apelido}</strong>
              {comparador && (
                <>
                  {' '}· pelo fluxo (parcelas + transferências): <strong>{fmtCheio(comparador.saldoFluxo)}</strong>
                  {' '}· pelo extrato bancário (até {fmtDataBr(comparador.ate)}): <strong>{fmtCheio(comparador.saldoExtrato)}</strong>
                  {' '}· diferença: <Text strong style={{ color: Math.abs(comparador.delta) > 1 ? '#b06a00' : '#0a7f4a' }}>{fmtCheio(comparador.delta)}</Text>
                </>
              )}
              {resp.conta.tipo !== 'corrente' && ' · conta sem extrato importado — saldo parte da âncora informada.'}
            </Text>
          }
          description={(() => {
            const sc = resp.conta!.sem_conta;
            const partes: string[] = [];
            if ((sc.realizado_cr + sc.realizado_cp) > 0) partes.push(`${sc.realizado_cr + sc.realizado_cp} lançamento(s) realizados no período sem conta definida`);
            if ((sc.previsto_cr + sc.previsto_cp) > 0) partes.push(`${sc.previsto_cr + sc.previsto_cp} previsto(s) em aberto sem conta`);
            if (sc.emprestimos_previstos > 0) partes.push(`${sc.emprestimos_previstos} parcela(s) de empréstimo (sem conta — fora desta visão)`);
            return partes.length
              ? <Text type="secondary" style={{ fontSize: 11 }}>Fora desta visão: {partes.join(' · ')}. Defina a conta na parcela (CP/CR) pra fechar a soma das contas com o consolidado.</Text>
              : undefined;
          })()}
        />
      )}

      {totais && (
        <Row gutter={10} style={{ marginBottom: 16 }}>
          <Col xs={12} md={8} lg={4}>
            <Card size="small" styles={{ body: { borderLeft: '4px solid #888' } }}>
              <Statistic title="Saldo inicial" value={fmt(totais.saldo_inicial)} valueStyle={{ color: '#888', fontSize: 18 }} />
            </Card>
          </Col>
          <Col xs={12} md={8} lg={4}>
            <Card size="small" styles={{ body: { borderLeft: '4px solid #0a7f4a' } }}>
              <Statistic title="Entradas" value={fmt(totais.entradas)} valueStyle={{ color: '#0a7f4a', fontSize: 18 }} />
              {temPrevisto && (totais.entradas_previstas || 0) > 0 && (
                <Text type="secondary" style={{ fontSize: 10 }}>+ {fmt(totais.entradas_previstas || 0)} previstas</Text>
              )}
            </Card>
          </Col>
          <Col xs={12} md={8} lg={4}>
            <Card size="small" styles={{ body: { borderLeft: '4px solid #B91626' } }}>
              <Statistic title="Saídas" value={fmt(totais.saidas)} valueStyle={{ color: '#B91626', fontSize: 18 }} />
              {temPrevisto && (totais.saidas_previstas || 0) > 0 && (
                <Text type="secondary" style={{ fontSize: 10 }}>+ {fmt(totais.saidas_previstas || 0)} previstas</Text>
              )}
            </Card>
          </Col>
          <Col xs={12} md={8} lg={4}>
            <Card size="small" styles={{ body: { borderLeft: `4px solid ${totais.liquido >= 0 ? '#0a7f4a' : '#B91626'}` } }}>
              <Statistic title="Líquido do período" value={fmt(totais.liquido)} valueStyle={{ color: totais.liquido >= 0 ? '#0a7f4a' : '#B91626', fontSize: 18 }} />
            </Card>
          </Col>
          <Col xs={12} md={8} lg={4}>
            <Card size="small" styles={{ body: { borderLeft: `4px solid ${totais.saldo_final >= 0 ? '#0930D0' : '#B91626'}` } }}>
              <Statistic title="Saldo final (realizado)" value={fmt(totais.saldo_final)} valueStyle={{ color: totais.saldo_final >= 0 ? '#0930D0' : '#B91626', fontSize: 18, fontWeight: 700 }} />
            </Card>
          </Col>
          {temPrevisto && (
            <Col xs={12} md={8} lg={4}>
              <Card size="small" styles={{ body: { borderLeft: `4px dashed ${(totais.saldo_projetado_final ?? 0) >= 0 ? '#0930D0' : '#B91626'}` } }}>
                <Statistic title="Saldo projetado" value={fmt(totais.saldo_projetado_final ?? totais.saldo_final)} valueStyle={{ color: (totais.saldo_projetado_final ?? 0) >= 0 ? '#0930D0' : '#B91626', fontSize: 18, fontWeight: 700 }} />
                <Text type="secondary" style={{ fontSize: 10 }}>realizado + previsto em aberto</Text>
              </Card>
            </Col>
          )}
        </Row>
      )}

      {resp?.saldo_origem?.modo === 'forward_corte' && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message={
            <Text style={{ fontSize: 11 }}>
              <strong>✓ Saldo inicial calculado desde a data de corte.</strong>{' '}
              Saldo em <strong>{fmtDataBr(resp.saldo_origem.data_corte || '')}</strong> (condição
              de contorno): <strong>{fmt(resp.saldo_origem.saldo_corte || 0)}</strong>.
              Acumulado até {fmtDataBr(resp.periodo.data_ini)}:
              {' +'}{fmt(resp.saldo_origem.entradas_acumuladas || 0)} entradas
              {' '}−{fmt(resp.saldo_origem.saidas_acumuladas || 0)} saídas
              {' '}= ajuste de {fmt(resp.saldo_origem.ajuste_aplicado || 0)}.
            </Text>
          }
        />
      )}
      {resp?.saldo_origem?.modo === 'reverso_corte' && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={
            <Text style={{ fontSize: 11 }}>
              <strong>ℹ Período antes da data de corte.</strong>{' '}
              Saldo em {fmtDataBr(resp.saldo_origem.data_corte || '')}:
              {' '}<strong>{fmt(resp.saldo_origem.saldo_corte || 0)}</strong>. Reversão até
              {' '}{fmtDataBr(resp.periodo.data_ini)}: ajuste de
              {' '}{fmt(resp.saldo_origem.ajuste_aplicado || 0)}.
            </Text>
          }
        />
      )}
      {resp?.saldo_origem?.modo === 'roll_back' && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={
            <Text style={{ fontSize: 11 }}>
              <strong>⚠ Sem saldo na data de corte ({fmtDataBr(corteCfg?.data_corte || '2026-01-01')}).</strong>{' '}
              Usando rollback do saldo de {fmtDataBr(resp.saldo_origem.data_ref || '')}:
              {' '}<strong>{fmt(resp.saldo_origem.saldo_em_data_ref || 0)}</strong>
              {' '}− {fmt(resp.saldo_origem.entradas_no_intervalo || 0)} entradas
              {' '}+ {fmt(resp.saldo_origem.saidas_no_intervalo || 0)} saídas
              {' '}= ajuste {fmt(resp.saldo_origem.ajuste_aplicado || 0)}.{' '}
              <a onClick={() => setEditandoCorte(true)} style={{ cursor: 'pointer' }}>Configure o saldo na data de corte →</a>
            </Text>
          }
        />
      )}

      <Card size="small" style={{ marginBottom: 16 }}>
        {loading && !resp ? (
          <Flex justify="center" align="center" style={{ height: 320 }}>
            <Text type="secondary">Carregando…</Text>
          </Flex>
        ) : !resp || resp.pontos.length === 0 ? (
          <Empty
            description={
              <>
                <div>Sem movimentação no período.</div>
                <Text type="secondary" style={{ fontSize: 10 }}>
                  {modo === 'diario'
                    ? `Nenhum recebimento ou pagamento em ${mes}.`
                    : `Nenhuma movimentação no ano de ${ano}.`}
                </Text>
              </>
            }
            style={{ padding: 60 }}
          />
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            {/* stackOffset="sign": positivos (entradas) empilham ACIMA do zero e
                negativos (saidas) ABAIXO — sem o vermelho cruzar o zero. */}
            <ComposedChart data={chartData} stackOffset="sign" margin={{ top: 10, right: 16, left: 0, bottom: 8 }}>
              <CartesianGrid stroke="#eee" strokeDasharray="3 3" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => fmt(v).replace('R$', '').trim()} />
              <Tooltip formatter={(value: any, name: any, item: any) => {
                if (value == null) return ['—', name];
                const n = typeof value === 'number' ? value : parseFloat(value);
                // abs só nas barras (saída vem negativa pelo stackOffset="sign");
                // saldo pode ser negativo DE VERDADE — preserva o sinal. Série
                // com name próprio chega aqui como nome de EXIBIÇÃO ("Saldo
                // acumulado"), então o teste confiável é o dataKey do item.
                const chave = item?.dataKey || name;
                const v = (chave === 'saldo' || chave === 'saldo_proj') ? n : Math.abs(n);
                const label = name === 'entradas' ? 'Entradas'
                            : name === 'saidas'   ? 'Saídas'
                            : name === 'saldo'    ? 'Saldo acumulado'
                            : name === 'entradas_prev' ? 'Entradas previstas'
                            : name === 'saidas_prev'   ? 'Saídas previstas'
                            : name === 'saldo_proj'    ? 'Saldo projetado' : name;
                return [fmtCheio(v), label];
              }} labelFormatter={(label) => `${modo === 'diario' ? 'Dia' : 'Mês'} ${label}`} />
              <Legend wrapperStyle={{ fontSize: 11 }} formatter={(v: any) => (
                v === 'entradas_prev' ? 'Entradas previstas'
                : v === 'saidas_prev' ? 'Saídas previstas'
                : v === 'saldo_proj'  ? 'Saldo projetado' : v
              )} />
              <ReferenceLine y={0} stroke="#aaa" strokeWidth={1} />
              {/* Mesmo stackId nos 4 → entrada (sobe) e saída (desce) alinhadas
                  na mesma linha vertical de cada mês/dia (diverge do zero). */}
              <Bar dataKey="entradas" name="Entradas" stackId="fluxo" fill="#0a7f4a" maxBarSize={30}>
                {chartData.map((d, i) => (
                  <Cell key={i} cursor="pointer" onClick={() => abrirDrillDown(d.bucket)} />
                ))}
              </Bar>
              {temPrevisto && (
                <Bar dataKey="entradas_prev" name="entradas_prev" stackId="fluxo" fill="#8fd4b0" maxBarSize={30}>
                  {chartData.map((d, i) => (
                    <Cell key={i} cursor="pointer" onClick={() => abrirDrillDown(d.bucket)} />
                  ))}
                </Bar>
              )}
              <Bar dataKey="saidas" name="Saídas" stackId="fluxo" fill="#B91626" maxBarSize={30}>
                {chartData.map((d, i) => (
                  <Cell key={i} cursor="pointer" onClick={() => abrirDrillDown(d.bucket)} />
                ))}
              </Bar>
              {temPrevisto && (
                <Bar dataKey="saidas_prev" name="saidas_prev" stackId="fluxo" fill="#eba3ab" maxBarSize={30}>
                  {chartData.map((d, i) => (
                    <Cell key={i} cursor="pointer" onClick={() => abrirDrillDown(d.bucket)} />
                  ))}
                </Bar>
              )}
              {/* connectNulls=false: o realizado para em hoje; do dia
                  seguinte em diante quem continua é só a tracejada. */}
              <Line type="monotone" dataKey="saldo" name="Saldo acumulado"
                stroke="#0930D0" strokeWidth={2} dot={{ r: 3 }} connectNulls={false} />
              {temPrevisto && (
                <Line type="monotone" dataKey="saldo_proj" name="saldo_proj"
                  stroke="#0930D0" strokeWidth={2} strokeDasharray="6 4"
                  dot={{ r: 2, strokeDasharray: '' }} connectNulls={false} />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </Card>

      {resp && pontosCompletos.length > 0 && (
        <Table
          rowKey="bucket"
          columns={pontosCols}
          dataSource={pontosCompletos}
          size="small"
          pagination={false}
          onRow={(r) => ({
            // Previsto também abre (2026-08-24). A linha só respondia ao clique
            // quando havia realizado — justamente nos dias futuros, onde só
            // existe previsto, não havia porta nenhuma pro detalhe.
            onClick: () => temMovimento(r) && abrirDrillDown(r.bucket),
            style: { cursor: temMovimento(r) ? 'pointer' : 'default' },
          })}
        />
      )}

      <Modal
        open={!!drillData}
        onCancel={() => setDrillData(null)}
        footer={null}
        width="94vw"
        style={{ maxWidth: 1100, top: 16 }}
        title={`Lançamentos · ${drillData ? (modo === 'diario' ? fmtDataBr(drillData) : _labelBucket(drillData, 'mensal')) : ''}`}
        destroyOnClose
      >
        {/* Realizado × previsto lado a lado: o gráfico mostra os dois, e aqui
            é onde a pessoa vem descobrir DE QUEM é o previsto daquele ponto. */}
        {drillTotais && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, marginBottom: 10 }}>
            <div>
              <Text type="secondary" style={{ fontSize: 11 }}>Já passou pelo banco</Text>
              <div style={{ fontSize: 12 }}>
                <Text style={{ color: '#0a7f4a' }}>↓ {fmtCheio(drillTotais.realizado_entradas)}</Text>
                {'  '}
                <Text style={{ color: '#B91626' }}>↑ {fmtCheio(drillTotais.realizado_saidas)}</Text>
              </div>
            </div>
            <div>
              <Text type="secondary" style={{ fontSize: 11 }}>Previsto (ainda não entrou/saiu)</Text>
              <div style={{ fontSize: 12, opacity: 0.75 }}>
                <Text style={{ color: '#0a7f4a' }}>↓ {fmtCheio(drillTotais.previsto_entradas)}</Text>
                {'  '}
                <Text style={{ color: '#B91626' }}>↑ {fmtCheio(drillTotais.previsto_saidas)}</Text>
              </div>
            </div>
            <div style={{ flex: 1, minWidth: 240 }}>
              <Text type="secondary" style={{ fontSize: 10 }}>
                O previsto aparece na data estimada de caixa (NF a emitir: emissão
                prevista + prazo; boleto na rua: vencimento; vencido: hoje) — a mesma
                do gráfico. Parcela de empréstimo não tem conta: some ao filtrar por banco.
              </Text>
            </div>
          </div>
        )}
        <Table
          rowKey={(r) => `${r.origem}-${r.tipo}-${r.id}`}
          columns={drillCols}
          dataSource={drillLancs}
          loading={drillLoading}
          size="small"
          pagination={false}
          scroll={{ y: 'clamp(240px, calc(100vh - 320px), 720px)' }}
          locale={{ emptyText: 'Sem lançamentos.' }}
        />
      </Modal>

      <Modal
        open={editandoCorte}
        onCancel={() => setEditandoCorte(false)}
        onOk={salvarCorte}
        okText="Salvar"
        cancelText="Cancelar"
        title="Data de corte do fluxo de caixa"
        width={520}
        destroyOnClose
      >
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
          A data de corte é a "condição de contorno" do fluxo. Cadastre o saldo
          nessa data uma vez e o sistema calcula o saldo inicial de qualquer mês
          posterior somando entradas e saídas até lá (forward-propagation).
          Mais robusto que recalcular do passado pra frente.
        </Text>
        {corteCfg?.saldo_corte && (
          <Alert
            type="success"
            message={
              <Text style={{ fontSize: 11 }}>
                Atual: <strong>{fmtCheio(parseFloat(corteCfg.saldo_corte.valor) || 0)}</strong> em{' '}
                <strong>{fmtDataBr(corteCfg.saldo_corte.data_ref)}</strong>
              </Text>
            }
            style={{ marginBottom: 12 }}
          />
        )}
        <Form layout="vertical" size="small">
          <Form.Item label="Data de corte" required>
            <DatePicker
              value={corteForm.data_corte ? dayjs(corteForm.data_corte) : null}
              onChange={(d: Dayjs | null) => setCorteForm({ ...corteForm, data_corte: d ? d.format('YYYY-MM-DD') : '' })}
              style={{ width: '100%' }}
              format="DD/MM/YYYY"
            />
          </Form.Item>
          <Form.Item label="Saldo nessa data (opcional — se for diferente do atual)">
            <InputNumber {...PROPS_DINHEIRO_BR}
              value={corteForm.valor ? Number(corteForm.valor) : null}
              onChange={(v) => setCorteForm({ ...corteForm, valor: v != null ? String(v) : '' })}
              precision={2}
              style={{ width: '100%' }}
              prefix="R$"
              placeholder="Deixe em branco pra manter o saldo atual"
            />
          </Form.Item>
          <Form.Item label="Observação (opcional)">
            <Input
              value={corteForm.observacao}
              onChange={e => setCorteForm({ ...corteForm, observacao: e.target.value })}
              placeholder="Ex: saldo bancário consolidado das 15 contas em 01/01/2026"
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={editandoSaldo}
        onCancel={() => setEditandoSaldo(false)}
        onOk={salvarSaldoInicial}
        okText="Salvar"
        cancelText="Cancelar"
        title="Saldo inicial"
        width={480}
        destroyOnClose
      >
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
          Define o saldo de caixa em uma data de referência. O cálculo do fluxo
          acumulado começa desse valor. Se já existir um saldo cadastrado nessa
          data, o valor é sobrescrito.
        </Text>
        {saldoVigente && saldoVigente.data_ref && (
          <Alert
            type="info"
            message={
              <Text style={{ fontSize: 11 }}>
                Vigente: <strong>{fmtCheio(saldoVigente.valor)}</strong> a partir de <strong>{fmtDataBr(saldoVigente.data_ref)}</strong>
              </Text>
            }
            style={{ marginBottom: 12 }}
          />
        )}
        <Form layout="vertical" size="small">
          <Form.Item label="Data de referência" required>
            <DatePicker
              value={saldoForm.data_ref ? dayjs(saldoForm.data_ref) : null}
              onChange={(d: Dayjs | null) => setSaldoForm({ ...saldoForm, data_ref: d ? d.format('YYYY-MM-DD') : '' })}
              style={{ width: '100%' }}
              format="DD/MM/YYYY"
            />
          </Form.Item>
          <Form.Item label="Valor do saldo (R$)" required>
            <InputNumber {...PROPS_DINHEIRO_BR}
              value={saldoForm.valor ? Number(saldoForm.valor) : null}
              onChange={(v) => setSaldoForm({ ...saldoForm, valor: v ? String(v) : '' })}
              precision={2}
              style={{ width: '100%' }}
              prefix="R$"
            />
          </Form.Item>
          <Form.Item label="Observação (opcional)">
            <Input
              value={saldoForm.observacao}
              onChange={e => setSaldoForm({ ...saldoForm, observacao: e.target.value })}
              placeholder="Ex: saldo bancário em 01/01/2026"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default function FinanceiroFluxoCaixa() {
  return (
    <AntApp>
      <FinanceiroFluxoCaixaInner />
    </AntApp>
  );
}
